<template>
  <div class="card">
    <h3 class="card-title">{{ $t('colorList.title') }}</h3>
    
    <div v-if="!hasExtractedColors" class="no-colors">
      <p>{{ $t('colorList.noColors') }}</p>
    </div>
    
    <div v-else class="color-list-section">
      <div class="color-list-header">
        <div class="color-info">
          <p>{{ $t('colorList.extractedCount') }} <strong>{{ uniqueColors.length }}</strong> {{ $t('colorList.colors') }}</p>
          <p class="text-muted">{{ $t('colorList.deduplicated') }}</p>
        </div>
        <div class="copy-controls">
          <button 
            class="btn-primary" 
            @click="copyAllColors"
            :disabled="uniqueColors.length === 0"
          >
            {{ $t('colorList.copyAll') }}
          </button>
          <button 
            class="btn-secondary" 
            @click="copyAllColorsHex"
            :disabled="uniqueColors.length === 0"
          >
            {{ $t('colorList.copyHex') }}
          </button>
        </div>
      </div>
      
      <div class="color-grid">
        <div 
          v-for="(color, index) in uniqueColors" 
          :key="`${color.r}-${color.g}-${color.b}`"
          class="color-item"
          @click="copySingleColor(color)"
          :title="`${$t('colorList.clickToCopy')} ${color.hex}`"
        >
          <div 
            class="color-preview" 
            :style="{ backgroundColor: color.hex }"
          ></div>
          <div class="color-info">
            <div class="color-name">{{ color.name || `颜色${index + 1}` }}</div>
            <div class="color-values">
              <span class="rgb">RGB({{ color.r }}, {{ color.g }}, {{ color.b }})</span>
              <span class="hex">{{ color.hex }}</span>
            </div>
          </div>
          <div class="copy-hint">{{ $t('preview.clickToCopy') }}</div>
        </div>
      </div>
      
      <div v-if="copyResult" class="copy-result" :class="copyResult.type">
        <span>{{ copyResult.message }}</span>
        <button class="close-btn" @click="copyResult = null">×</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useColorStore } from '../stores/colorStore'
import { rgbToHex } from '../utils/colorUtils'
import type { RGBColor } from '../types'

const colorStore = useColorStore()

const copyResult = ref<{ type: 'success' | 'error'; message: string } | null>(null)

const hasExtractedColors = computed(() => !!colorStore.extractedColors)

// 获取去重后的颜色列表
const uniqueColors = computed(() => {
  if (!colorStore.extractedColors) return []
  
  const colorMap = new Map<string, RGBColor>()
  
  // 遍历所有像素，收集唯一颜色
  for (let y = 0; y < colorStore.extractedColors.height; y++) {
    for (let x = 0; x < colorStore.extractedColors.width; x++) {
      const pixel = colorStore.extractedColors.pixels[y][x]
      const key = `${pixel.r}-${pixel.g}-${pixel.b}`
      
      if (!colorMap.has(key)) {
        colorMap.set(key, {
          ...pixel,
          hex: rgbToHex(pixel.r, pixel.g, pixel.b),
          name: `颜色${colorMap.size + 1}`
        })
      }
    }
  }
  
  // 转换为数组并按RGB值排序
  return Array.from(colorMap.values()).sort((a, b) => {
    // 按RGB值排序，让相似颜色聚集
    const aSum = a.r + a.g + a.b
    const bSum = b.r + b.g + b.b
    return aSum - bSum
  })
})

// 复制单个颜色
async function copySingleColor(color: RGBColor & { hex: string }) {
  try {
    await navigator.clipboard.writeText(color.hex)
    showCopyResult('success', `已复制颜色: ${color.hex}`)
  } catch (error) {
    console.error('复制失败:', error)
    showCopyResult('error', '复制失败，请手动复制')
  }
}

// 复制所有颜色（RGB格式）
async function copyAllColors() {
  try {
    const colorText = uniqueColors.value
      .map(color => `RGB(${color.r}, ${color.g}, ${color.b})`)
      .join(', ')
    
    await navigator.clipboard.writeText(colorText)
    showCopyResult('success', `已复制 ${uniqueColors.value.length} 种颜色的RGB值`)
  } catch (error) {
    console.error('复制失败:', error)
    showCopyResult('error', '复制失败，请手动复制')
  }
}

// 复制所有颜色（16进制格式）
async function copyAllColorsHex() {
  try {
    const hexText = uniqueColors.value
      .map(color => color.hex)
      .join(', ')
    
    await navigator.clipboard.writeText(hexText)
    showCopyResult('success', `已复制 ${uniqueColors.value.length} 种颜色的16进制值`)
  } catch (error) {
    console.error('复制失败:', error)
    showCopyResult('error', '复制失败，请手动复制')
  }
}

// 显示复制结果
function showCopyResult(type: 'success' | 'error', message: string) {
  copyResult.value = { type, message }
  
  // 3秒后自动清除
  setTimeout(() => {
    copyResult.value = null
  }, 3000)
}
</script>

<style scoped>
.no-colors {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.color-list-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.color-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.color-info p {
  margin: 0.25rem 0;
}

.color-info strong {
  color: #667eea;
}

.text-muted {
  color: #6c757d;
  font-size: 0.875rem;
}

.copy-controls {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.color-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

.color-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.color-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  border-color: #667eea;
}

.color-preview {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 2px solid #dee2e6;
  flex-shrink: 0;
}

.color-info {
  flex: 1;
  min-width: 0;
}

.color-name {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.color-values {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-size: 0.75rem;
}

.rgb {
  color: #6c757d;
}

.hex {
  color: #667eea;
  font-weight: 500;
}

.copy-hint {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.color-item:hover .copy-hint {
  opacity: 1;
}

.copy-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  animation: slideIn 0.3s ease;
}

.copy-result.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.copy-result.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.close-btn {
  background: none;
  border: none;
  color: inherit;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.close-btn:hover {
  background: rgba(0,0,0,0.1);
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .color-list-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .copy-controls {
    justify-content: center;
  }
  
  .color-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }
  
  .color-item {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
  
  .color-preview {
    width: 60px;
    height: 60px;
  }
}
</style>
